import { useEffect, useState } from "react";

const PROJECT_STORIES = [
  {
    id: 1,
    title: "Regenta Hotel",
    description:
      "A landmark hospitality project blending luxury with architectural precision and modern aesthetics.",
    image: "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835296/Regenta_pjxiny.webp",
    category: "Hospitality",
    year: "2024",
  },
  {
    id: 2,
    title: "Hotel Kapish",
    description:
      "Redefining city living with thoughtful design and community-focused amenities.",
    image: "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835023/Kapish_bpifai.jpg",
    category: "Architectural Design",
    year: "2024",
  },
  {
    id: 3,
    title: "GRASS Field Valley",
    description:
      "A contemporary design that bridges natural landscapes with innovative built form.",
    image: "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835023/Grassfield_ng6tmp.jpg",
    category: "Public Architecture",
    year: "2024",
  },
  {
    id: 4,
    title: "Premium Residence 5",
    description:
      "A dynamic residential space celebrating creativity and bringing the community together.",
    image: "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835022/PR5_1_ttert3.jpg",
    category: "Residential",
    year: "2024",
  },
  {
    id: 5,
    title: "Dhai Dweep",
    description:
      "An island-inspired retreat where architecture harmonizes with nature and water.",
    image: "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835022/Dhai_dweep_1_htdglg.jpg",
    category: "Cultural",
    year: "2023",
  },
  {
    id: 6,
    title: "Concept Vision 2026",
    description:
      "A futuristic architectural concept pushing the boundaries of form, light, and space.",
    image: "https://res.cloudinary.com/dkwsduc5l/image/upload/v1778835024/ChatGPT_Image_Apr_20_2026_03_27_20_PM_towz0v.png",
    category: "Concept Design",
    year: "2026",
  },
];

const ProjectStories = ({ autoplay = true, projects = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const items = projects.length > 0 ? projects : PROJECT_STORIES;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Track viewport so the carousel can drop the peeking side cards below `sm`
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const goTo = (index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const nextSlide = () => goTo((currentIndex + 1) % items.length);
  const prevSlide = () => goTo((currentIndex - 1 + items.length) % items.length);

  useEffect(() => {
    if (!autoplay) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [currentIndex, autoplay]);

  const getPosition = (index) => {
    if (index === currentIndex) return "center";
    if (index === (currentIndex - 1 + items.length) % items.length) return "left";
    if (index === (currentIndex + 1) % items.length) return "right";
    return "hidden";
  };

  const current = items[currentIndex];

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 via-yellow-50 to-amber-50 py-16 sm:py-24 overflow-hidden relative">
      {/* Background blobs */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-amber-300 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 rounded-full border border-yellow-200 mb-4 sm:mb-6">
            <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-yellow-700 font-semibold text-xs sm:text-sm uppercase tracking-widest">
              Our Portfolio
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-3 sm:mb-4 leading-tight px-2">
            Featured <span className="text-yellow-500">Architectural</span> Projects
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed px-2">
            Masterpieces that blend innovative design, sustainability, and functionality.
          </p>
          <div className="mt-5 sm:mt-6 w-16 sm:w-20 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 mx-auto rounded-full" />
        </div>

        {/* Carousel */}
        <div className={`relative h-[340px] xs:h-[380px] sm:h-[520px] md:h-[580px] transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex justify-center items-center relative h-full">
            {items.map((project, index) => {
              const position = getPosition(index);
              if (position === "hidden") return null;
              // On mobile only the center card renders — no peeking side cards to clip or overlap
              if (isMobile && position !== "center") return null;

              let scale = 1;
              let offset = "0%";
              let zIndex = 10;
              let opacity = "opacity-100";
              let blur = "";

              if (position === "left") {
                scale = 0.8;
                offset = "-82%";
                zIndex = 5;
                opacity = "opacity-40";
                blur = "blur-sm";
              } else if (position === "right") {
                scale = 0.8;
                offset = "82%";
                zIndex = 5;
                opacity = "opacity-40";
                blur = "blur-sm";
              }

              return (
                <div
                  key={project.id}
                  onClick={() => position !== "center" && goTo(index)}
                  className={`absolute top-0 left-1/2 ${opacity} ${blur} transition-all duration-700 ease-in-out ${position !== "center" ? "cursor-pointer" : ""}`}
                  style={{
                    width: isMobile ? "100%" : "80%",
                    height: "100%",
                    transform: `translateX(calc(-50% + ${isMobile ? "0%" : offset})) scale(${isMobile ? 1 : scale})`,
                    zIndex,
                  }}
                >
                  <div className="relative w-full h-full group rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
                    {/* Image */}
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />

                    {/* Bottom gradient overlay — always on center card */}
                    {position === "center" && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    )}

                    {/* Bottom info strip — center only */}
                    {position === "center" && (
                      <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 py-4 sm:py-6">
                        <h3 className="text-lg sm:text-2xl font-bold text-white mb-1 tracking-tight">
                          {project.title}
                        </h3>
                        <p className="text-white/70 text-xs sm:text-sm leading-relaxed line-clamp-2">
                          {project.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Nav Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-yellow-50 border border-yellow-200 text-yellow-600 hover:text-yellow-700 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-yellow-50 border border-yellow-200 text-yellow-600 hover:text-yellow-700 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dot Indicators */}
        <div className={`flex justify-center mt-8 sm:mt-10 gap-2 transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "bg-yellow-500 w-8 h-3"
                  : "bg-slate-300 hover:bg-yellow-300 w-3 h-3"
              }`}
            />
          ))}
        </div>

        {/* Project name strip below dots */}
        <div className={`text-center mt-5 sm:mt-6 transition-all duration-500 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">
            {current.category} · {current.year}
          </p>
          <h4 className="text-lg sm:text-xl font-semibold text-slate-700">{current.title}</h4>
        </div>

      </div>
    </div>
  );
};

export default ProjectStories;